// 一键成稿
import { notUseReferenceColorWeight, defaultBatchSize, defaultAspectRatios, defaultHD, defaultPerturb } from "../default";
import { ModuleEnum } from "../moduleEnum";
import type { LayoutReferState } from "@/types/image";
import type { Tag } from "@/types/prompt";
import { getModelEventTrackType, getStyleRefer, gethdFix } from "../index";
import { compressImage } from "@/utils/image";
import { getImageUrlSize, getSimilarAspectRatios, filteringInvalidFields } from "@/utils";
import { useEventTracking } from "@/hooks/useEventTracking";
import type { ModelState } from "@/types/model";
import { startModuleGenerate } from "./index";
import { convertDownloadUrl, convertToJpg } from "@xmiles/holopix-helper";
import { uploadFilePromise } from "@/utils/aliyunUpload";
import { registerUploadedAsset } from "@/hooks/useAssetUpload";
import { useModelStore } from "@/store/model";
import { useNewUserGuide } from "@/store/newUserGuide";
import { getModuleLabelById } from "@xmiles/holopix-helper";
import { handleEventTracking } from "@/hooks/useEventTracking/eventvalue-util";
import { useArtworkStore } from "@/store/artwork";
import { useUserStore } from "@/store/user";
import { poseWeight } from "../default";
import { defaultModelId } from "@/assets/artwork";
import { getUserModelId } from "@/views/workspace/hooks/common";
import { isEmpty as isEmptyObject } from "lodash";
import { styleModelOptions } from '@/constants';
import { mapExploreCkptTypeForTracking } from '@/assets/artwork/module/a3/exploreTracking';
import { mapSketchTypeForTracking } from '@/constants/sketchTypeLabels';
import { applyExploreA3Params, isExploreA3Ckpt } from './a3/applyExploreA3Params';



export const module = ModuleEnum["explore"];

// 生图参数
export const exploreParams = {
  module,
  advancedMode: false, // 高级模式（已弃用）
  imgTags: [] as Tag[], // 图片提示词标签
  prompt: "", // 用户输入的提示词
  imgTag: "", // 图像识别提示词
  negativePrompt: "", // 反向提示词
  negativePromptWeight: 0.4, // 反向提示词权重
  layoutReferId: null as null | number, // 原始稿件图片id
  layoutRefer: null as null | LayoutReferState, // 原始稿件
  layoutReferPose: null as null | LayoutReferState, // 任务姿势图片
  layoutReferMode: "draft" as "draft" | "pose", // 布局结构参考（draft: 草图，pose: 姿态）
  poseMode: "norm" as "norm" | "myself", // 当 layoutReferMode 为姿势时（norm: 官方，myself: 我的）
  maskImage: null as null | LayoutReferState, // 原始稿件蒙板图片
  maskContrastImage: null as null | LayoutReferState, // 原始稿件蒙板反相图片
  sketchType: 1, // 布局参考图类型：1-线稿，2-色块
  layoutReferWeight: notUseReferenceColorWeight, // 布局参考图权重
  colorRefer: false, // 参考颜色
  styleReferList: [] as any[], // 形象样式参考图片
  styleReferType: 1, // 形象样式参考风格类型（1: 默认 2: 构图 3: 风格 4: 脸部）
  styleReferWeight: 0.4, // 形象样式参考权重
  simpleBackground: true, // 简单画面背景
  faceDetail: true, // 面部精修
  aspectRatios: defaultAspectRatios, // 尺寸比例
  batchSize: defaultBatchSize, // 生成数量
  hdFix: false, // 开启高清修复
  hdScale: defaultHD, // 高清修复数值
  enablePerturb: false, // 开启画面增强
  perturb: defaultPerturb, // 画面增强
  alchemy: false, // 语义增强引擎(Flux专用)
  imageGuidanceWeights: 2, // 画面指导权重
  colorSketchVer: 1 // 色块参考版本
};

// 形象样式参考类型
export const styleTypeOptions = [
  {
    label: "默认",
    value: 1,
    multiple: true // 支持多图
  },
  {
    label: "构图",
    value: 2,
    multiple: true
  },
  {
    label: "风格",
    value: 3,
    multiple: true
  },
  {
    label: "人脸",
    value: 4,
    multiple: false // 不支持多图
  }
];

// A1模型样式参考类型
export const styleTypeOptionsA1 = [
  {
    label: "标准",
    value: 1,
    multiple: false // 支持多图
  },
  {
    label: "氛围颜色",
    value: 3,
    multiple: false
  }
];

let isNewbieTask = false;

// 将当前用户输入的生图参数转换成后端所需要
export async function getExploreGenerateParam() {
  const artworkStore = useArtworkStore();
  const userStore = useUserStore();

  // 安全检查：确保 modelList 存在且有数据
  if (isEmptyObject(artworkStore.artworkState.modelList)) {
    let modelId = defaultModelId;
    try {
      if (userStore.isLogin) {
        modelId = await getUserModelId();
      }
    } catch (e) {
      modelId = defaultModelId;
    }
    await artworkStore.updateArtwork({
      modelList: [{ id: modelId }] as any
    });
  }

  let params = {
    module: exploreParams.module,
    userId: userStore.userInfo.id,
    uniqueId: userStore.userInfo.uniqueId,
    artworkId: artworkStore.artworkState.id,
    modelList: artworkStore.artworkState.modelList,
    loraList: artworkStore.artworkState.modelList.map((item) => {
      return {
        modelId: item.id,
        strength: item.strength
      };
    }),
    source: 1
  } as any;

  const layoutRefer =
    artworkStore.explore.params?.layoutReferMode === "draft" ? artworkStore.explore.params.layoutRefer : artworkStore.explore.params.layoutReferPose;
  const layoutReferId = layoutRefer?.id;
  let aspectRatios = artworkStore.explore.params?.aspectRatios;

  if (artworkStore.explore.params?.layoutReferMode === "draft") {
    params.layoutReferPose = null;
  } else {
    params.layoutRefer = null;
    params.layoutReferWeight = poseWeight;
  }

  if (layoutRefer?.image) {
    const { width, height } = await getImageUrlSize(layoutRefer.image);
    aspectRatios = getSimilarAspectRatios(width, height);
  }

  const newUserGuideStore = useNewUserGuide();

  isNewbieTask = newUserGuideStore.isAbleFreeGenerateImage;

  newUserGuideStore.isAbleFreeGenerateImage = false;

  params = {
    ...artworkStore.explore.params,
    ...params,
    aspectRatios: aspectRatios,
    /**
     * 首次通过一键成稿生图，B组用户不扣算力
     */
    firstGene: isNewbieTask ? 1 : 0,
    styleReferIdList: (artworkStore.explore.params?.styleReferList || []).map((item:any) => {
      return {
        styleReferId: item.id,
        weight: item.weight / 100
      };
    }),
    layoutReferId: layoutReferId || null
  };

  params.maskId = artworkStore.explore.params?.maskContrastImage?.id || null;
  if (artworkStore.explore.params?.layoutReferMode !== "draft" || artworkStore.explore.params?.sketchType !== 2) {
    delete params.maskImage;
    delete params.maskContrastImage;
    params.maskId = null;
  }

  // 图片提示词：统一拼 enName（与 A2 一致；§5.4 中文能力不由前端分叉处理）
  params.imgTag =
    artworkStore.explore.params?.imgTags
      ?.map((item: any) => item.enName)
      .filter(Boolean)
      .join(",") || "";

  // 去除 layoutRefer 中的无效字段
  filteringInvalidFields(params.layoutRefer, ["id", "width", "height", "image"]);

  // Flux 模型特殊处理：清理不兼容参数
  const isFluxModel = params.modelList[0]?.ckptType === "Flux";
  if (isFluxModel) {
    params.negativePrompt = params.alchemy ? params.negativePrompt : "";

    // 移除局部细化参数
    delete params.maskImage;
    delete params.maskContrastImage;
    params.maskId = null;

    // 移除形象样式参考参数
    params.styleReferList = [];
    params.styleReferIdList = [];

    // V1 Flux模型：已回退到原版逻辑，不再强制设置画面指导权重
    // 由 DraftTab 组件根据版本和参考颜色自动计算权重
  }

  // A3：切 module=125，并映射对接字段（originImgUrl / denoise / width·height 等）
  if (isExploreA3Ckpt(params.modelList[0]?.ckptType)) {
    applyExploreA3Params(params);
  }

  // 处理图片不超过2000*2000
  if (params.layoutRefer) {
    params.layoutRefer = await compressImage(params.layoutRefer);
    params.layoutReferId = params.layoutRefer.id;

    // 将 layoutRefer 转为 JPG 格式（白底），非 JPG 时重新上传注册
    const lowerUrl = params.layoutRefer.image?.toLowerCase() ?? '';
    if (!lowerUrl.endsWith('.jpg') && !lowerUrl.endsWith('.jpeg')) {
      try {
        // 先将 CDN URL 转为直连 OSS 加速域名，确保 x-oss-process 参数可靠处理
        const directUrl = convertDownloadUrl(params.layoutRefer.image);
        const jpgUrl = convertToJpg(directUrl);
        const response = await fetch(jpgUrl);
        // 检查 OSS 处理是否成功，避免将空/错误响应上传为空白图片
        if (!response.ok) {
          throw new Error(`OSS 转 JPG 失败: ${response.status} ${response.statusText}`);
        }
        const blob = await response.blob();
        const jpgFile = new File([blob], 'converted.jpg', { type: 'image/jpeg' });
        const ossUrl = await uploadFilePromise(jpgFile, { namescape: 'explore-jpg' });
        const record = await registerUploadedAsset(jpgFile, ossUrl, 4, artworkStore.artworkState.id);
        if (record) {
          params.layoutRefer = {
            id: record.id ?? 0,
            image: record.image ?? ossUrl,
            width: record.width ?? params.layoutRefer.width,
            height: record.height ?? params.layoutRefer.height,
          };
          params.layoutReferId = params.layoutRefer.id;
        }
      } catch (err) {
        console.error('一键成稿图片转 JPG 失败，使用原始图片:', err);
      }
    }
  }

  // 处理图片蒙版不超过2000*2000
  if (params.maskImage) {
    params.maskImage = await compressImage(params.maskImage);
    params.maskContrastImage = await compressImage(params.maskContrastImage);
    params.maskId = params.maskContrastImage.id;
  }

  // A3 的 originImgUrl 来自 layoutRefer，必须在参考图压缩 / 转 JPG 重传之后同步，
  // 否则会带上压缩前的旧地址（对接 §2 要求 originImgUrl 为实际上传的参考图）
  if (isExploreA3Ckpt(params.modelList?.[0]?.ckptType) && params.layoutRefer) {
    params.originImgUrl = params.layoutRefer.image ?? params.originImgUrl;
    params.originImgUrlId = params.layoutReferId ?? params.originImgUrlId;
  }

  return params;
}

/**
 * 构建埋点公共参数
 * 说明：聚合开始/结束埋点中相同字段，保持字段一致性与可维护性
 * @param moduleId 模块 ID
 * @param params 当前生成参数
 * @param artworkStore 作品 Store 实例
 * @param modelIdList 模型 ID 列表
 * @param modelTagList 模型标签文本列表
 * @param modelTypeList 模型类型文本列表
 * @param isUserGeneratedText 是否为用户模型（是/否/混合）
 * @param eventParms 其他埋点扩展参数
 * @returns 公共埋点参数对象
 */
function buildCommonEventValue(
  moduleId: number,
  params: any,
  artworkStore: ReturnType<typeof useArtworkStore>,
  modelIdList: number[],
  modelTagList: string[],
  modelTypeList: string[],
  isUserGeneratedText: string,
  eventParms: Record<string, any>
) {
  return {
    module: getModuleLabelById(moduleId) as string,
    source: params.source === 1 ? "直接生成" : "再迭代",
    artwork_id: artworkStore.artworkState.id?.toString?.() ?? "",
    prompt: params.prompt,
    negative_prompt: params.negativePrompt,
    face_detail: params.faceDetail ? 1 : 0,
    simplebg: params.simpleBackground ? 1 : 0,
    style_refer: params.styleReferList?.length > 0 ? "1" : "0",
    hp_model_id: String(modelIdList.join(",")),
    model_name: params.modelList.map((item: ModelState) => item.modelName).join(",") || JSON.stringify(params.modelList || ["--"]),
    ckpt_type: params.modelList
      .map((item: ModelState) => mapExploreCkptTypeForTracking(item.ckptType))
      .join(","),
    lora_type: params.modelList.map((item: ModelState) => item.loraType).join(","),
    is_personal_model: getModelEventTrackType(params.modelList),
    is_team_share_works: artworkStore.artworkState.shareType === 1 ? 1 : 0,
    model_tags: modelTagList.join(","),
    model_type: modelTypeList.join(","),
    hp_is_user_generated: isUserGeneratedText,
    ...eventParms
  } as any;
}

/**
 * 依据当前参数为埋点追加条件字段
 * 说明：统一构建差异字段，避免在开始/结束埋点中重复逻辑
 * 包含：sketch_type（仅 creation_event）、pose、style_refer、hd_fix、part_modify、semantics_enhance、is_newbie_task
 * @param eventValue 事件对象（将被就地扩充）
 * @param params 当前生成参数
 * @param options.includeSketchType 是否上报参考类型（0917：仅提交生图上报，结果展示不重复）
 * @returns 扩充后的事件对象
 */
function applyConditionalEventFields(
  eventValue: any,
  params: any,
  options: { includeSketchType?: boolean } = {}
) {
  const { includeSketchType = true } = options;
  if (includeSketchType && params.layoutReferId && params.layoutReferMode === "draft") {
    // 色块参考类型：沿用历史枚举文案「1-线稿 / 2-色块」
    eventValue.sketch_type = mapSketchTypeForTracking(Number(params.sketchType));
  }
  if (params.layoutReferId && params.layoutReferMode === "pose") {
    // 姿势参考图片 ID（用于人物姿势埋点）
    eventValue.pose = params.layoutReferId;
  }
  const styleRefer = getStyleRefer(params.styleReferType, params.styleReferList);
  if (styleRefer) {
    // 形象样式参考（根据类型映射后的埋点值覆盖）
    eventValue.style_refer = styleRefer;
  }
  const hdFix = gethdFix(params.hdFix, params.hdScale);
  if (hdFix) {
    // 高清修复（开启与修复倍数）
    eventValue.hd_fix = hdFix;
  }
  if (params.layoutReferMode === "draft") {
    // 局部细化（草图模式下：有蒙版=0，无蒙版=1）
    eventValue.part_modify = params.maskImage ? 0 : 1;
  }
  // 语义增强开关（Flux 专用）
  eventValue.semantics_enhance = params.alchemy ? 1 : 0;
  // 是否为新手任务（首图免算力）
  eventValue.is_newbie_task = isNewbieTask;
  return eventValue;
}

// 开启模块生图
export async function startExploreGenerate(
  oldParams?: any,
  options?: {
    onClientIdGenerated?: (clientId: string) => void;
    onClientIdCleared?: () => void;
  }
) {
  const artworkStore = useArtworkStore();
  const modelStore = useModelStore();

  const params = await getExploreGenerateParam();
  if (!params) {
    return false as any;
  }
  const module = exploreParams.module;

  const modelList = artworkStore.artworkState.modelList || [];
  const modelLabels = modelList.flatMap((item) => item.label || []);
  // 按照 modelLabels 中 ID 的顺序过滤和排序标签
  const modelTagList = modelLabels
    .map((labelId) => modelStore.labelOptions.find((item: any) => item.value === labelId))
    .filter(Boolean)
    .map((item) => item!.label);

  const modelCategories = modelList.map((item) => item.category).filter(Boolean);

  const modelTypeList = modelCategories
    .map((categoryId) => modelStore.categoryOptions.find((item: any) => item.value === categoryId))
    .filter(Boolean)
    .map((item) => item!.label);

  const isUserGeneratedText = (() => {
    if (modelList.every((i) => i.userId !== 0)) return "是";
    if (modelList.every((i) => i.userId === 0)) return "否";
    return "混合";
  })();
  const modelIdList = params.modelList.map((item: ModelState) => item.id);

  const eventParms = handleEventTracking(modelList);

  // 提交生图埋点
  function reportStartEventTracking(clientId: string) {
    const commonValue: any = buildCommonEventValue(
      module,
      params,
      artworkStore,
      modelIdList,
      modelTagList,
      modelTypeList,
      isUserGeneratedText,
      eventParms
    );
    const eventValue: any = {
      client_id: clientId,
      batchSize: params.batchSize,
      ...commonValue
    };
    applyConditionalEventFields(eventValue, params);

    useEventTracking({
      event: "creation_event",
      eventValue
    });
  }

  function reportFinishEventTracking({
    client_id = "",
    result = 0 as 0 | 1,
    imageIds = "",
    queue_time_number = 0,
    loading_time_number = 0,
    vipChannel = false
  } = {}) {
    const commonValue: any = buildCommonEventValue(
      module,
      params,
      artworkStore,
      modelIdList,
      modelTagList,
      modelTypeList,
      isUserGeneratedText,
      eventParms
    );
    const eventValue: any = {
      client_id,
      img_id: imageIds,
      result,
      queue_time_number,
      loading_time_number,
      first_model_ckpt: mapExploreCkptTypeForTracking(params.modelList[0]?.ckptType),
      vipChannel: vipChannel,
      ...commonValue
    };

    // 结果展示不重复上报 sketch_type（0917）
    applyConditionalEventFields(eventValue, params, { includeSketchType: false });

    useEventTracking({
      event: "artwork_status_event",
      eventValue
    });
  }

  /**
   * 开始生成
   *
   */
  startModuleGenerate(module, {
    getModuleParams: () => {
      return oldParams || params;
    },
    onStartTrack: (clientId) => {
      reportStartEventTracking(clientId);
    },
    onFinishTrack: (data) => {
      reportFinishEventTracking(data);
    },
    onClientIdGenerated: options?.onClientIdGenerated,
    onClientIdCleared: options?.onClientIdCleared
  });
}
